import { getISOWeek } from 'date-fns'
import { slugify } from './slugify.js'
import {
  EDITORIAL_INFO_TYPE,
  EDITORIAL_INFO_TYPE_NAME,
  editorialInfoTypeKey,
  namesEditorialInfoType
} from './schema.js'
import type { MemoTemplate, MemoVariant } from './schema.js'

/**
 * What a title pattern is resolved against: the date the memo covers, the last
 * date of the span for a template that covers a range, the section for a
 * template written per section, and the variant for a template that comes in
 * more than one shape.
 */
export interface MemoTitleOptions {
  date: Date
  toDate?: Date
  /**
   * The section the memo is written *for*, which is not the section it is filed
   * in: a framtids-PM across the desk is filed in Inrikes and written for no
   * section at all. A template with a per-section naming takes that one when
   * this is set, and its own when it is not.
   */
  sectionTitle?: string
  localeCode?: string
  variant?: MemoVariant
}

/**
 * Which of a template's namings a memo is written under: the selected variant's,
 * the per-section one for a memo written for a section, or the template's own.
 *
 * Decided in one place so that a memo's title and its slugline cannot end up
 * under different namings - a "TT:s utrikes vecko-pm" sluglined "vecko-pm".
 * The schema rejects a template carrying both a variant and a per-section
 * naming, so the order here is a precedence in name only.
 */
export function memoNaming(
  template: MemoTemplate,
  options: { sectionTitle?: string, variant?: MemoVariant }
): { titlePattern: string, slugline: string } {
  const { sectionTitle, variant } = options

  if (variant) {
    return variant
  }

  if (sectionTitle && template.planning.section) {
    return template.planning.section
  }

  return template.planning
}

/**
 * A memo's assignment is titled after its editorial-info type where the
 * template has one, so the chosen type's title comes along on top of everything
 * a memo title is otherwise resolved against.
 */
export interface MemoAssignmentTitleOptions extends MemoTitleOptions {
  editorialInfoTypeTitle?: string
}

/** Day and month as digits, e.g. "27.8". */
function shortDate(date: Date): string {
  return `${date.getDate()}.${date.getMonth() + 1}`
}

function sameMonth(from: Date, to: Date): boolean {
  return from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()
}

/**
 * A span of days as digits, e.g. "31.7-2.8". A span within one month names the
 * month once - "1-3.8" - the way the sluglines are written by hand.
 */
function shortDateRange(from: Date, to: Date): string {
  if (from.getTime() === to.getTime()) {
    return shortDate(from)
  }

  if (sameMonth(from, to)) {
    return `${from.getDate()}-${shortDate(to)}`
  }

  return `${shortDate(from)}-${shortDate(to)}`
}

/**
 * A span of days written out, e.g. "31 juli-2 augusti 2026". A span within one
 * month names the month once - "1-3 augusti 2026" - and the year is named once
 * at the end unless the span crosses into a new one.
 */
function dateRange(from: Date, to: Date, localeCode: string): string {
  const dayMonth = (date: Date) => date.toLocaleDateString(localeCode, {
    day: 'numeric',
    month: 'long'
  })
  const year = (date: Date) => String(date.getFullYear())

  if (from.getTime() === to.getTime()) {
    return `${dayMonth(from)} ${year(from)}`
  }

  if (sameMonth(from, to)) {
    return `${from.getDate()}-${dayMonth(to)} ${year(to)}`
  }

  if (from.getFullYear() === to.getFullYear()) {
    return `${dayMonth(from)}-${dayMonth(to)} ${year(to)}`
  }

  return `${dayMonth(from)} ${year(from)}-${dayMonth(to)} ${year(to)}`
}

/**
 * Resolve the date placeholders a title pattern and a slugline share. Both are
 * written about the same span, so both understand the same names.
 *
 * `{dayName}` is the weekday of the first covered day as the locale writes it,
 * e.g. "torsdag" - the indefinite form, as opposed to the definite one a title
 * pattern's `{weekday}` resolves to. It lives here rather than with the titles
 * because a slugline names the day too: "TT-torsdag-pm 3.9".
 *
 * `{week}` is the ISO week of the first covered day, two digits, e.g. "05".
 * ISO weeks are what the Swedish calendar numbers.
 */
export function resolveMemoDates(
  pattern: string,
  options: MemoTitleOptions
): string {
  const { date, toDate } = options
  const localeCode = options.localeCode ?? 'sv-SE'
  const last = toDate ?? date

  return pattern
    .replaceAll('{dayName}', date.toLocaleDateString(localeCode, { weekday: 'long' }))
    .replaceAll('{fromDate}', shortDate(date))
    .replaceAll('{toDate}', shortDate(last))
    .replaceAll('{shortDateRange}', shortDateRange(date, last))
    .replaceAll('{dateRange}', dateRange(date, last, localeCode))
    .replaceAll('{year}', String(last.getFullYear()))
    .replaceAll('{week}', String(getISOWeek(date)).padStart(2, '0'))
}

/**
 * Resolve the section placeholders a title pattern and a slugline share, the
 * way `resolveMemoDates` does the dates - a memo written for one section names
 * it in both.
 *
 * `{sectionTitle}` is the section as written, e.g. "Utrikes", and
 * `{sectionLower}` the same in lower case, e.g. "utrikes" - the memos are
 * written both ways, the vecko-PM naming its section mid-sentence.
 *
 * `{sectionInitial}` is its first letter in lower case, e.g. "u", and
 * `{sectionSlug}` the whole of it slugified, e.g. "kultur-noje" for "Kultur &
 * Nöje": a slugline is lower case whatever case the datastore holds the section
 * title in, and holds neither the spaces nor the ampersand a title can.
 *
 * All four resolve to nothing for a memo written for no section, leaving the
 * caller to tidy up whatever gap that opens.
 */
export function resolveMemoSections(
  pattern: string,
  sectionTitle: string | undefined,
  localeCode = 'sv-SE'
): string {
  const title = sectionTitle?.trim() ?? ''

  return pattern
    .replaceAll('{sectionTitle}', title)
    .replaceAll('{sectionLower}', title.toLocaleLowerCase(localeCode))
    .replaceAll('{sectionInitial}', title.charAt(0).toLocaleLowerCase(localeCode))
    .replaceAll('{sectionSlug}', slugify(title))
}

/**
 * Resolve a template's `planning.titlePattern`.
 *
 * Supported placeholders:
 *  - `{weekday}` - the weekday in definite form, e.g. "tisdagen"
 *  - `{dayName}` - the weekday as written, e.g. "tisdag"
 *  - `{date}` - day and month, e.g. "25 augusti"
 *  - `{fromDate}` - the first day of the span as digits, e.g. "27.8"
 *  - `{toDate}` - the last day of the span as digits, e.g. "28.9"
 *  - `{shortDateRange}` - the span as digits, e.g. "31.7-2.8" or "1-3.8"
 *  - `{dateRange}` - the span written out, e.g. "31 juli-2 augusti 2026"
 *  - `{year}` - the year the span ends in, e.g. "2026"
 *  - `{week}` - the ISO week the span starts in, two digits, e.g. "05"
 *  - `{sectionTitle}` - the section as written, e.g. "Utrikes"
 *  - `{sectionLower}` - the same in lower case, e.g. "utrikes"
 *
 * A template with variants is written about the same span in more than one way
 * - the weekend PM covers a Friday or a Saturday onwards - and the selected
 * variant's pattern is used in place of the template's own. So is the
 * per-section pattern of a template that has one, for a memo written for a
 * section - see `memoNaming`.
 *
 * Swedish forms the definite weekday by suffixing "-en" (tisdag -> tisdagen),
 * which holds for all seven days. Other locales get the plain weekday.
 */
export function resolveMemoTitle(template: MemoTemplate, options: MemoTitleOptions): string {
  const { sectionTitle, variant } = options
  const { titlePattern } = memoNaming(template, { sectionTitle, variant })

  return resolveTitlePattern(titlePattern, options)
}

/**
 * Resolve one title pattern against a memo's span and section.
 *
 * Shared by the memo's title and the heading the PM itself opens with, so that
 * the two understand the same names - which of them a template writes a given
 * day into is a matter of where it wants the day said, not of what it may say.
 * Every placeholder listed on `resolveMemoTitle` resolves here.
 *
 * Collapsing the whitespace is what tidies up after a placeholder that resolved
 * to nothing, a pattern naming a section on a memo written for none leaving a
 * gap where the section would have stood.
 */
function resolveTitlePattern(pattern: string, options: MemoTitleOptions): string {
  const { date, sectionTitle } = options
  const localeCode = options.localeCode ?? 'sv-SE'

  const weekday = date.toLocaleDateString(localeCode, { weekday: 'long' })
  const definiteWeekday = localeCode.startsWith('sv') ? `${weekday}en` : weekday
  const day = date.toLocaleDateString(localeCode, { day: 'numeric', month: 'long' })

  return resolveMemoSections(resolveMemoDates(pattern, options), sectionTitle, localeCode)
    .replaceAll('{weekday}', definiteWeekday)
    .replaceAll('{date}', day)
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Resolve what the PM itself is headed with, which is a field of its own in the
 * creation dialog: the editor writes the headline the PM goes out under without
 * that renaming the planning it is filed in or the assignment it is written on.
 *
 * A template declaring no `memoTitlePattern` is headed with what its planning
 * is called, the two being the same thing said once until the editor says
 * otherwise. Declaring one is for a template that needs them to differ from the
 * start - see the schema.
 *
 * A pattern may name `{editorialInfoType}`, which is what the print PM heads
 * itself with: the type is what that memo is about, so "PM BNL torsdagen 3
 * september" is what the desk writes where another memo writes "TT:s morgon-pm
 * torsdagen 3 september". It is the type's title as written, spaces and all -
 * unlike `resolveMemoAssignmentTitle`, which hyphenates the same string because
 * that one is a key in a publish pipe and this is a sentence someone reads.
 *
 * Such a heading resolves to nothing until a type is picked, rather than to a
 * day with a hole where the type would stand. The picker sits in the form
 * beside the preview the field is in, so that is what it holds to begin with,
 * and creation is blocked without a type anyway - the same reason the
 * assignment title starts out empty.
 *
 * What comes back is a suggestion and not the heading: the editor writes over
 * it in the preview. So it is resolved against the same span and section as the
 * title, which is what keeps moving the dates moving the untouched heading too.
 *
 * A type listed in `editorialInfoTypeHeadings` is headed with that entry's
 * pattern instead, which may also name `{editorialInfoTypeName}` - the title
 * without its "PM " prefix, e.g. "BNL".
 */
export function resolveMemoHeading(
  template: MemoTemplate,
  options: MemoAssignmentTitleOptions
): string {
  const typeTitle = (options.editorialInfoTypeTitle ?? '').trim()
  const pattern = headingPattern(template, typeTitle)

  if (!pattern) {
    return resolveMemoTitle(template, options)
  }

  if (namesEditorialInfoType(pattern) && !typeTitle) {
    return ''
  }

  return resolveTitlePattern(
    pattern
      .replaceAll(EDITORIAL_INFO_TYPE_NAME, editorialInfoTypeName(typeTitle))
      .replaceAll(EDITORIAL_INFO_TYPE, typeTitle),
    options
  )
}

/**
 * An editorial-info type's title without the "PM " prefix every type is named
 * with, e.g. "BNL" for "PM BNL".
 */
export function editorialInfoTypeName(title: string): string {
  return title.trim().replace(/^pm[\s-]+/i, '')
}

function headingPattern(template: MemoTemplate, typeTitle: string): string | undefined {
  const key = editorialInfoTypeKey(typeTitle)
  const match = key
    ? template.editorialInfoTypeHeadings.find(
      (heading) => heading.types.some((type) => editorialInfoTypeKey(type) === key)
    )
    : undefined

  return match?.memoTitlePattern ?? template.memoTitlePattern
}

/**
 * Resolve the title suggested for a memo's assignment.
 *
 * Usually that is the memo's own title - the planning, the assignment and the
 * memo all describe the same PM - but a template funneled into a publish pipe
 * by its editorial-info type is named after that type instead: the print desk
 * writes a "PM-BNL", not a "TT:s print-pm torsdagen 8 september".
 *
 * The type's title is taken verbatim apart from its spaces, which become
 * hyphens: "PM BNL" -> "PM-BNL". Case and diacritics are what the type is
 * called and stay as they are - unlike `resolveMemoSlugline`, which slugifies
 * the same string because a slugline can hold neither.
 *
 * The type is picked below the title field, so a template that requires one has
 * nothing to title its assignment with until then and is left empty rather than
 * suggesting a title the editor would have to clear. Creation is blocked
 * without a type anyway, so an empty assignment title can never be saved.
 */
export function resolveMemoAssignmentTitle(
  template: MemoTemplate,
  options: MemoAssignmentTitleOptions
): string {
  if (!template.requiresEditorialInfoType) {
    return resolveMemoTitle(template, options)
  }

  return (options.editorialInfoTypeTitle ?? '').trim().replace(/\s+/g, '-')
}
